import { DmkNetworkClient } from "@ledgerhq/device-management-kit";
import { inject, injectable } from "inversify";
import { Either, Left, Right } from "purify-ts";

import { configTypes } from "@/config/di/configTypes";
import { type ContextModuleServiceConfig } from "@/config/model/ContextModuleConfig";
import { networkTypes } from "@/shared/network/di/networkTypes";

import { SafeProxyImplementationAddressDto } from "./dto/SafeProxyImplementationAddressDto";
import {
  GetProxyImplementationAddressParam,
  type ProxyDataSource,
  ProxyImplementationAddress,
} from "./ProxyDataSource";

@injectable()
export class HttpSafeProxyDataSource implements ProxyDataSource {
  constructor(
    @inject(configTypes.Config)
    private readonly config: ContextModuleServiceConfig,
    @inject(networkTypes.NetworkClient)
    private readonly http: DmkNetworkClient,
  ) {}

  async getProxyImplementationAddress({
    proxyAddress,
    chainId,
    challenge,
  }: GetProxyImplementationAddressParam): Promise<
    Either<Error, ProxyImplementationAddress>
  > {
    let dto: SafeProxyImplementationAddressDto | undefined;
    try {
      dto = (await this.http.get(
        `${this.config.metadataServiceDomain.url}/v3/ethereum/${chainId}/contract/proxy/${proxyAddress}`,
        {
          params: {
            challenge,
            resolver: "SAFE_GATEWAY",
          },
        },
      )) as SafeProxyImplementationAddressDto;
    } catch (_error) {
      return Left(
        new Error(
          `[ContextModule] HttpSafeProxyDataSource: Failed to fetch safe proxy implementation`,
        ),
      );
    }

    if (!dto) {
      return Left(
        new Error(
          `[ContextModule] HttpSafeProxyDataSource: No data received for proxy ${proxyAddress} on chain ${chainId}`,
        ),
      );
    }

    if (!this.isSafeProxyImplementationAddressDto(dto)) {
      return Left(
        new Error(
          `[ContextModule] HttpSafeProxyDataSource: Invalid safe proxy response format for proxy ${proxyAddress} on chain ${chainId}`,
        ),
      );
    }

    return Right({
      implementationAddress: dto.implementationAddress,
      signedDescriptor: dto.signedDescriptor,
      keyId: dto.keyId,
      keyUsage: dto.keyUsage,
    });
  }

  /**
   * Type guard to validate SafeProxyImplementationAddressDto
   */
  private isSafeProxyImplementationAddressDto(
    value: unknown,
  ): value is SafeProxyImplementationAddressDto {
    return (
      typeof value === "object" &&
      value !== null &&
      "proxyAddress" in value &&
      "implementationAddress" in value &&
      "standard" in value &&
      "signedDescriptor" in value &&
      "providedBy" in value &&
      "keyId" in value &&
      "keyUsage" in value &&
      typeof value.proxyAddress === "string" &&
      typeof value.implementationAddress === "string" &&
      typeof value.standard === "string" &&
      typeof value.signedDescriptor === "string" &&
      typeof value.providedBy === "string" &&
      typeof value.keyId === "string" &&
      typeof value.keyUsage === "string"
    );
  }
}
